import { Fragment, useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Drawer,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import HandoverPage from '@/designs-dev/handover/HandoverPage';
import {
  builtPending,
  hasChanges,
  isBuilt,
  versionName,
  highlighted,
  type VersionedSpec,
} from '@/designs-dev/handover/versions';
import { CHROME_ZOOM } from '@/designs-dev/inspect/chrome';
import { Segment } from '@/designs-dev/inspect/Pill';

// A flows page with a history: the newest version by default, any other by
// ?v=, so an older one is a link that can be shared. See versions.ts.
//
// The history stays out of the way. The breadcrumb names the version on screen
// and opens a panel with every version and what each one changed; the page
// itself shows only the design.
const VersionedHandoverPage = ({ spec }: { spec: VersionedSpec }) => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // Highlight what the version changed. Off by default, and in the URL so a
  // highlighted view can be shared.
  const highlight = params.get('changes') === '1';
  const setHighlight = (on: boolean) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (on) next.set('changes', '1');
        else next.delete('changes');
        return next;
      },
      { replace: true },
    );
  const latest = spec.versions[spec.versions.length - 1];
  // Which versions' accordions are open. The latest starts open: what changed
  // most recently is what the panel is usually opened to read.
  const [expanded, setExpanded] = useState<string[]>([latest.n]);
  const asked = params.get('v');
  const current = spec.versions.find((v) => v.n === asked) ?? latest;
  const isLatest = current === latest;
  // How far main has got: this version is either on main or still to build.
  const onMain = isBuilt(spec, current.n);
  const status = onMain ? 'On main' : 'Not built yet';
  const pending = builtPending(spec);

  // The same page with only the version changed, so a scene or drill in the
  // URL survives the switch.
  const hrefFor = (n: string) => {
    const next = new URLSearchParams(params);
    if (n === latest.n) next.delete('v');
    else next.set('v', n);
    const q = next.toString();
    return q ? `?${q}` : '?';
  };

  const version = (
    <Stack direction='row' spacing={2} sx={{ alignItems: 'center' }}>
      {/* A quiet link rather than a button: the version is a fact about the
          page that happens to open its history, not an action competing with
          the design for attention. */}
      <Link
        component='button'
        variant='overline'
        underline='hover'
        onClick={() => setOpen(true)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          // Red with the link beside it, on any version but the latest.
          color: isLatest ? 'text.primary' : 'error.main',
          textDecorationColor: 'currentColor',
        }}
      >
        {versionName(current)}
        <Icon icon={'chevron-down' as never} fontSize={10} />
      </Link>
      <Typography variant='overline' color='text.secondary'>
        {status}
      </Typography>
      {!isLatest && (
        // Red, so an older version can't be mistaken for the current design,
        // and the whole line is the way back.
        <Link
          component={RouterLink}
          to={hrefFor(latest.n)}
          variant='overline'
          underline='always'
          // The underline in the same red: MUI draws it from the primary
          // colour unless told otherwise.
          sx={{ color: 'error.main', textDecorationColor: 'currentColor' }}
        >
          View the latest version
        </Link>
      )}
    </Stack>
  );

  // On a canvas the version lives in the floating bar with the page's other
  // controls, so the breadcrumb only says which version this is. A page that
  // opts out of the canvas has no bar, and keeps the link.
  const onCanvas = current.spec.canvas !== false;
  const label = (
    <Typography
      variant='overline'
      sx={{ color: isLatest ? 'text.primary' : 'error.main' }}
    >
      {versionName(current)}
    </Typography>
  );
  const tools = (
    <>
      <Segment
        label='Versions'
        // Red on any version but the latest, as the breadcrumb was.
        tone={isLatest ? undefined : 'error'}
        onClick={() => setOpen(true)}
      >
        {versionName(current)}
      </Segment>
      {/* A label, not a control: whether this version is on main. */}
      <Segment
        label={pending ? `${status}: PR #${pending} isn't on main yet` : status}
        icon={onMain ? 'check' : undefined}
      >
        {status}
      </Segment>
      {!isLatest && (
        <Segment
          label='View the latest version'
          tone='error'
          onClick={() => navigate(hrefFor(latest.n))}
        >
          Latest
        </Segment>
      )}
      {hasChanges(current.spec) && (
        <Segment
          label='Highlight changes'
          selected={highlight}
          onClick={() => setHighlight(!highlight)}
        >
          Highlight changes
        </Segment>
      )}
    </>
  );

  // Every version as one history, newest first, each with what it is and what it
  // changed. The earlier ones open their own pages; the rest switch this one.
  const history = [
    ...(spec.earlier ?? []).map((v) => ({
      ...v,
      draft: false,
      to: v.url,
      here: false,
      built: isBuilt(spec, v.n),
    })),
    ...spec.versions.map((v) => ({
      n: v.n,
      note: v.note,
      changes: v.changes,
      draft: v.draft,
      to: hrefFor(v.n),
      here: true,
      built: isBuilt(spec, v.n),
    })),
  ].reverse();

  const panel = (
    <Drawer anchor='right' open={open} onClose={() => setOpen(false)}>
      <Stack spacing={3} sx={{ width: 480, p: 3, zoom: CHROME_ZOOM }}>
        <Stack direction='row' sx={{ alignItems: 'center' }}>
          <Typography variant='h4' sx={{ flex: 1 }}>
            Versions
          </Typography>
          <IconButton aria-label='Close' onClick={() => setOpen(false)}>
            <Icon icon={'xmark' as never} fontSize={16} />
          </IconButton>
        </Stack>

        {/* Where main has got to, in a line: everything up to the built
            version is on main and locked, everything after it is still to
            build. */}
        <Typography variant='body2' color='text.secondary'>
          {spec.built && !pending
            ? `Built up to ${spec.built.n} (PR #${spec.built.pr}). A change to a built version is the next draft.`
            : pending
              ? `Marked built at ${spec.built?.n}, but PR #${pending} isn't on main yet.`
              : 'Nothing marked as built yet.'}
        </Typography>

        {/* One accordion per version, all shut: the list is for finding a
            version, and what it is and what it changed open on a click. The
            number is a link to the version, so it switches without opening. */}
        <Box>
          {history.map((v) => {
            const showing = v.here && v.n === current.n;
            const isOpen = expanded.includes(v.n);
            // The tags after the number, in one grey line joined by dots.
            const after = [
              v.here && v.n === latest.n && 'Latest',
              v.here && v.draft && 'Draft',
              v.built && 'On main',
              showing && 'Showing',
            ].filter(Boolean);
            return (
              <Accordion
                key={v.n}
                disableGutters
                expanded={isOpen}
                onChange={(_, now) =>
                  setExpanded((list) =>
                    now ? [...list, v.n] : list.filter((n) => n !== v.n),
                  )
                }
                // A flush list, not Loft's separate accordion cards: a history
                // can run long, so the versions sit close with one hairline
                // between them. Local to this drawer; the theme's accordion is
                // unchanged everywhere else. Showing is said by its tag now
                // there is no box to outline.
                sx={{
                  bgcolor: 'transparent',
                  border: 0,
                  borderBottom: 1,
                  borderColor: 'divider',
                  borderRadius: '0 !important',
                  '&:not(:last-of-type)': { mb: 0 },
                  '&.Mui-expanded': { m: 0 },
                  '&:last-of-type.Mui-expanded': { mb: 0 },
                }}
              >
                <AccordionSummary
                  sx={{ px: 0 }}
                  expandIcon={
                    <Icon icon={'chevron-down' as never} fontSize={14} />
                  }
                >
                  <Stack
                    direction='row'
                    spacing={1}
                    sx={{ alignItems: 'center', flex: 1, mr: 2 }}
                  >
                    <Typography variant='h6'>{v.n}</Typography>
                    {after.length > 0 && (
                      <Typography variant='caption' color='text.secondary'>
                        {` · ${after.join(' · ')}`}
                      </Typography>
                    )}
                    {/* Right-aligned, so every Restore sits in the same
                        column whatever tags come before it. On every version
                        but the one showing, open or shut. */}
                    {!showing && (
                      <Link
                        component={RouterLink}
                        to={v.to}
                        variant='caption'
                        underline='always'
                        color='text.secondary'
                        onClick={(e) => e.stopPropagation()}
                        sx={{ ml: 'auto !important' }}
                      >
                        Restore
                      </Link>
                    )}
                    {/* The version on screen gets Highlight changes in the same
                        column instead, when it has changes to show. */}
                    {showing && hasChanges(current.spec) && (
                      <Link
                        component='button'
                        variant='caption'
                        underline='always'
                        color='text.secondary'
                        onClick={(e) => {
                          e.stopPropagation();
                          setHighlight(!highlight);
                        }}
                        sx={{ ml: 'auto !important' }}
                      >
                        {highlight ? 'Hide changes' : 'Highlight changes'}
                      </Link>
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0 }}>
                  <Stack spacing={1}>
                    <Typography variant='body2' color='text.secondary'>
                      {v.note}
                    </Typography>
                    {v.changes && v.changes.length > 0 && (
                      <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
                        {v.changes.map((c) => (
                          <Typography key={c} component='li' variant='body2'>
                            {c}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Stack>
    </Drawer>
  );

  const page = (
    <HandoverPage
      spec={highlight ? highlighted(current.spec) : current.spec}
      version={onCanvas ? label : version}
      tools={onCanvas ? tools : undefined}
    />
  );

  // Keyed by version, so switching starts the page afresh rather than carrying
  // one version's opened rows and chat state into the other. The panel sits
  // outside the key, so it stays open while you flip between versions.
  return (
    <>
      <Fragment key={current.n}>
        {current.wrap ? current.wrap(page) : page}
      </Fragment>
      {panel}
    </>
  );
};

export default VersionedHandoverPage;
